export interface SipIdentityUri {
    user?: unknown;
    host?: unknown;
}

export interface SipRemoteIdentity {
    displayName?: unknown;
    friendlyName?: unknown;
    uri?: SipIdentityUri & { aor?: string; toString(): string };
}

export function callerNumber(
    uri: SipIdentityUri | null | undefined,
    fallback?: string
): string | undefined {
    const uriUser = typeof uri?.user === "string" ? uri.user.trim() : "";
    if (uriUser) return uriUser;

    const fallbackUser = fallback?.trim() ?? "";
    return fallbackUser || undefined;
}

export function callerDisplayName(
    uri: SipIdentityUri | null | undefined,
    fallback?: string
): string | undefined {
    return callerNumber(uri, fallback);
}

/** Presentation metadata only; never use the remote party's name as our SIP From identity. */
export function remoteCallerDisplayName(
    identity?: SipRemoteIdentity | null,
    assertedIdentity?: SipRemoteIdentity | null
): string | undefined {
    const fromName = identityDisplayName(identity);
    if (fromName || !assertedIdentity) return fromName;

    // SIP.js supplies a separately parsed assertedIdentity. Only use its label
    // for the same numeric party at the same PBX; never replace the routing URI.
    const user = identity?.uri?.user;
    const host = identity?.uri?.host;
    if (typeof user !== "string" || !/^\+?\d+$/.test(user) ||
        typeof host !== "string" || !host ||
        assertedIdentity.uri?.user !== user ||
        typeof assertedIdentity.uri?.host !== "string" ||
        assertedIdentity.uri.host.toLowerCase() !== host.toLowerCase())
        return undefined;

    // A missing/numeric From name or the legacy generated Unit label can use
    // this fallback. Other labels (including private/anonymous) remain From-only.
    const fromLabel = typeof identity?.displayName === "string" ? identity.displayName.trim() : "";
    const comparable = (value: string) => value.replace(/[\s().-]/g, "");
    if (fromLabel && comparable(fromLabel.replace(/^Unit\s+/i, "")) !== comparable(user))
        return undefined;

    return identityDisplayName(assertedIdentity);
}

function identityDisplayName(identity?: SipRemoteIdentity | null): string | undefined {
    const explicitName = typeof identity?.displayName === "string" ? identity.displayName.trim() : "";
    const friendlyName = typeof identity?.friendlyName === "string" ? identity.friendlyName.trim() : "";
    const candidate = explicitName || friendlyName;
    const name = Array.from(candidate.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim())
        .slice(0, 120).join("");
    if (!name) return undefined;

    const number = callerNumber(identity?.uri);
    const comparableNumber = (value: string) => value.replace(/[\s().-]/g, "");
    if (number && (comparableNumber(name) === comparableNumber(number) ||
        comparableNumber(name.replace(/^Unit\s+/i, "")) === comparableNumber(number)))
        return undefined;

    // SIP.js friendlyName falls back to URI.aor, which is not a person's name.
    if (name.toLowerCase() === identity?.uri?.aor?.toLowerCase() ||
        name.toLowerCase() === identity?.uri?.toString().toLowerCase() || /^(sips?|tel):/i.test(name))
        return undefined;
    return name;
}
